import { allRangeLabel } from "../../util/allRange";
import { ComponentProps, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";

import { averageSessionDurationConverter } from "../../util/helper";
import { getMetricsData } from "../../util/metricsModal";
import { getPlaybackDayMetrics } from "../../util/playbackModal";
import storage from "../../util/storage";
import { SESSION_DEFINITION } from "../../util/sessionDefinition";
import {
  getEffectiveDateRangeValue,
  selectedDateRange,
  selectedEndDateRange,
} from "../../util/clusterService";
import {
  mode as playbackModeValue,
  playbackDate as playbackDateValue,
  footerVisible as sharedFooterVisible,
  setFooterVisible,
} from "../../util/playbackModeService";

import { eventEmitter } from "../../eventEmitters/EventEmitters";

import ActionType from "../../resources/enums";

import DownIcon from "../../assets/images/down-icon.png";
import "./Footer.scss";
import Loader from "../../loader/Loader";
import { loaderHidden, loaderShown, refreshedQuietly } from "../../util/loaderStats";
import { useTranslation } from "react-i18next";
import { usePerfRender } from "../../util/perfProbe";

// The up / down arrows beside each number (the demo and builds with REACT_APP_FOOTER_TRENDS=1: the file is not imported, and so is not in the bundle, anywhere else; both are defined as 0 in .env for exactly this).
// eslint-disable-next-line @typescript-eslint/no-var-requires
const trendParts = (process.env.REACT_APP_DEMO === "1" || process.env.REACT_APP_FOOTER_TRENDS === "1") ? require("../../footerTrends/footerTrendParts") : null;
type TrendMetric = "pageViews" | "totalSession" | "percentNewSessions" | "pageviewsPerSession" | "bounceRate" | "avgSessionDuration" | "conversions" | "conversionRate";
/** a metric's hover text: its definition, and how it compares with the interval before (when there is a comparison to make) */
const tip = (key: TrendMetric, definition: string): string => (trendParts ? trendParts.tipFor(key, definition) : definition);
/** the arrows beside a metric's number (nothing in a build that has none) */
const trendArrows = (key: TrendMetric) => (trendParts ? <trendParts.TrendArrows metric={key} /> : null);

/*
 * 🔴 THE METRIC DEFINITIONS ARE react-tooltip NOW, NOT A BESPOKE CSS HOVER.
 *
 * They were a `.metric-tip` span positioned inside the metric's own <li>, at
 * z-index 1004, with the whole footer lifted to 1002 by a `:has(...)` rule
 * only while hovered. That is the incremental z-index chase Styles.scss
 * already describes losing: "kept getting reported covered by something else
 * each time". It lost again - the definitions stopped appearing in production
 * while both the markup and the CSS were still deployed and correct.
 *
 * Portalling to document.body removes the problem rather than out-bidding it.
 * The tooltip is no longer inside the footer's stacking context, so nothing
 * the footer overlaps can cover it, and `.react-tooltip`'s own z-index 99999
 * applies.
 *
 * It also answers the other half of the request: these now render in the SAME
 * component, with the SAME global styling, as the tooltips in the right-hand
 * drawer and the segment panel. One tooltip look across the app.
 *
 * Must stay MODULE-LEVEL. Declared inside Footer it would be a new component
 * type on every render, so React would unmount and remount every tooltip -
 * the exact note SessionRow.tsx carries on its own RowTooltip.
 */
const MetricTooltip = (props: ComponentProps<typeof Tooltip>) =>
  createPortal(<Tooltip positionStrategy="fixed" {...props} />, document.body);

const Footer = () => {
  usePerfRender("Footer");
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const dispatch = useDispatch();

  // The label follows the range the DATA was fetched for (selectedDateRange, restored from the saved choice on a refresh), not Redux, which is back to "Today"
  // on every load.
  const [currHistoricalFilter, setCurrHistoricalFilter] = useState(
    selectedDateRange?.name ?? authReducer?.historicalFilterSelected?.name
  );
  const { t: translation } = useTranslation();
  useEffect(() => {
    // Mounted while playback is open (coming back from Settings, say): the bar shows the PLAYBACK day, not live "today". It always
    // fetched live data here, so the bar came back zeroed and labelled "Today".
    if (playbackModeValue === "playback" && playbackDateValue) {
      // tslint:disable-next-line: no-floating-promises
      getPlaybackMetricsInfo(playbackDateValue);
    } else {
      getMetricsDataInfo(undefined, { trigger: "first load" });
    }

    metrixData();
    metrixDataOnFilterChange();
    metrixDataOnGoalConverted();
    metrixDataOnLiveActivity();
  }, []);

  const metrixData = () => {
    eventEmitter.addListener("newSession", () => {
      // Live-traffic events still fire while in playback mode (the socket
      // handlers just no-op their visualization side - see AnalyticsView.tsx)
      // but the Footer's numbers must stay locked to the picked playback
      // date, not get clobbered by live "today" data.
      if (playbackModeValue === "playback") {
        return;
      }
      getMetricsDataInfo("newSession");
    });
  };
  // Was only refreshing on newSession/goalConverted - avg session duration
  // and bounce rate both depend on a session ENDING (session_end_time), not
  // just arriving, so a departure alone never refreshed this Footer,
  // leaving it stale relative to the segment-detail panel (which already
  // refreshes on this broader event set - see RightMenu.tsx's own
  // onLiveActivity). Reported directly ("that goes for the bottom metrics
  // bar also"). Same ~1s throttle as that listener, for the same reason
  // (userCountChanged alone can fire on every single arrival/pageview/
  // departure).
  const metrixDataOnLiveActivity = () => {
    let lastLiveFooterFetchAt = 0;
    const LIVE_FOOTER_THROTTLE_MS = 1000;
    const onLiveActivity = () => {
      if (playbackModeValue === "playback") {
        return;
      }
      const now = Date.now();
      if (now - lastLiveFooterFetchAt < LIVE_FOOTER_THROTTLE_MS) {
        return;
      }
      lastLiveFooterFetchAt = now;
      getMetricsDataInfo("newSession");
    };
    eventEmitter.addListener("sessionChanged", onLiveActivity);
    eventEmitter.addListener("sessionFinished", onLiveActivity);
    eventEmitter.addListener("drawExistingUserBubble", onLiveActivity);
    eventEmitter.addListener("userCountChanged", onLiveActivity);
  };
  const metrixDataOnFilterChange = () => {
    // "filterChanged" is raised by the user (a filter or date range) AND by three automatic resyncs
    // (the 3-minute timer, returning to a tab hidden for 5s+, a socket reconnect), which pass
    // { quiet: true }. Only the first kind earns the full-screen loader: the numbers refresh
    // either way, but a loading screen that appears every few minutes with nothing asked for
    // reads as a glitch (Chris: "intermittent loading screens that flash really quickly").
    eventEmitter.addListener("filterChanged", (info?: { quiet?: boolean; trigger?: string }) => {
      if (playbackModeValue === "playback") {
        return;
      }
      getMetricsDataInfo(undefined, { quiet: !!info?.quiet, trigger: info?.trigger });
    });
  };
  const metrixDataOnGoalConverted = () => {
    // An existing session reaching a goal mid-visit only emits
    // "goalConverted" (clusterService.ts), not "newSession" - without this,
    // conversions/conversionRate stayed stale until a full page reload.
    eventEmitter.addListener("goalConverted", () => {
      if (playbackModeValue === "playback") {
        return;
      }
      getMetricsDataInfo("newSession");
    });
  };
  const [historicalLoader, setHistoricalLoader] = useState<boolean>(false);
  const [historyDataInfo, getHistoryDataInfo] = useState<any>();
  // the range the numbers are for, handed to the arrows: replaced only when it really changes (not on every refresh)
  const [trendRange, setTrendRange] = useState<{ name: string; startMs: number; endMs: number; ongoing: boolean } | null>(null);
  const [, setTrendTick] = useState(0);
  useEffect(() => {
    if (!trendParts) return undefined;

    return trendParts.subscribe(() => setTrendTick((n: number) => n + 1));
  }, []);
  useEffect(() => {
    if (!trendParts) return;
    trendParts.feed({
      current: historyDataInfo,
      range: trendRange,
      earliestMs: authReducer?.authData?.createdAt ? new Date(authReducer.authData.createdAt).getTime() : null,
      playback: playbackModeValue === "playback",
    });
  }, [historyDataInfo, trendRange]);
  /*
   * newSession: set by the live-event refreshes, which never raise the loader.
   * opts.quiet: set by the automatic resyncs (see the "filterChanged" listener), same.
   * Anything else is the user changing something, or the first load: the loader goes up
   * and is brought down in `finally`, so a failed fetch cannot leave it on screen.
   */
  const getMetricsDataInfo = async (
    newSession?: string,
    opts?: { quiet?: boolean; trigger?: string }
  ) => {
    const loud = !newSession && !opts?.quiet;
    if (loud) {
      setHistoricalLoader(true);
      loaderShown(opts?.trigger || "filter or date change");
    } else if (opts?.quiet) {
      refreshedQuietly();
    }
    try {
    const params = {
      endDate: selectedEndDateRange
      ? selectedEndDateRange
      : new Date().toISOString(),
      startDate:
      selectedDateRange && selectedDateRange.value
      ? new Date(getEffectiveDateRangeValue()).toISOString()
      : new Date(new Date().setHours(0,0,0,0)).toISOString(),
      siteId: authReducer.authData.site._id,
    };
    if (trendParts) {
      const next = {
        name: selectedDateRange?.name ?? "",
        startMs: new Date(params.startDate).getTime(),
        endMs: new Date(params.endDate).getTime(),
        ongoing: !selectedEndDateRange,
      };
      setTrendRange((prev) => (prev && prev.name === next.name && prev.startMs === next.startMs && prev.ongoing === next.ongoing && (next.ongoing || prev.endMs === next.endMs) ? prev : next));
    }
    const getHistoryData = await getMetricsData(params);
    dispatch({
      type: ActionType.TOTALSESSION,
      payload: { totalSession: getHistoryData?.totalSession },
    });
    storage.set("totalSession", getHistoryData?.totalSession);
    getHistoryDataInfo(getHistoryData);
    setCurrHistoricalFilter(selectedDateRange?.name ?? authReducer?.historicalFilterSelected.name);
    } finally {
      if (loud) {
        setHistoricalLoader(false);
        loaderHidden();
      }
    }
  };

  // Playback feature: same summary shape as getMetricsData (see
  // getPlaybackDayMetrics's backend comment), so historyDataInfo/JSX below
  // needs no branching beyond which fetch populates it.
  const [playbackDateForLabel, setPlaybackDateForLabel] = useState<string | null>(null);
  const getPlaybackMetricsInfo = async (date: string) => {
    setHistoricalLoader(true);
    const metrics = await getPlaybackDayMetrics(date);
    getHistoryDataInfo(metrics?.summary);
    setPlaybackDateForLabel(date);
    setHistoricalLoader(false);
  };

  const [showFooter, setShowFooter] = useState<boolean>(sharedFooterVisible);
  // Playback's ScrubBar/PlaybackControlBar are both fixed-positioned a
  // hardcoded distance above this footer (see their own .scss files) - they
  // have no way to know the footer just collapsed/expanded on their own, so
  // they'd otherwise leave a permanent gap (or overlap) where the footer
  // used to be. Broadcasting the collapsed state lets them shift down/up by
  // exactly the footer's own height to close that gap.
  // setFooterVisible (not a plain local emit) also mirrors the value into
  // playbackModeService's own module variable - ScrubBar only exists during
  // playback, so exiting to live and back unmounts/remounts it, and a
  // fresh mount needs to read the REAL current value instead of assuming
  // "visible" by default (see that module's own comment on why the event
  // alone can't cover this - a listener that starts listening after the
  // fact never sees an already-fired change).
  useEffect(() => {
    setFooterVisible(showFooter);
  }, [showFooter]);
  const [isPlaybackMode, setIsPlaybackMode] = useState(playbackModeValue === "playback");
  useEffect(() => {
    const onModeChanged = ({
      mode,
      playbackDate,
    }: {
      mode: string;
      playbackDate: string | null;
    }) => {
      setIsPlaybackMode(mode === "playback");
      if (mode === "playback" && playbackDate) {
        getPlaybackMetricsInfo(playbackDate);
      } else if (mode === "live") {
        getMetricsDataInfo(undefined, { trigger: "back to live" });
      }
    };
    eventEmitter.addListener("modeChanged", onModeChanged);
    return () => {
      eventEmitter.removeListener("modeChanged", onModeChanged);
    };
  }, []);

  return (
    <>
      {/* Fixed at the viewport's own bottom-right corner and NEVER moves -
          previously this button repositioned itself (bottom:0 <-> bottom:64,
          then later top:-22 relative to the footer) to track the footer's
          own collapse animation, which is exactly what let it wander into
          playback's ScrubBar (fixed at bottom:66px) and get covered.
          Anchoring it at bottom:0 keeps it permanently below the ScrubBar's
          own footprint regardless of collapsed/expanded state, so there's
          nothing left to collide with - only the arrow icon's rotation
          reflects state, the button's position never does. */}
      <button
        className="hide-footer-btn"
        onClick={() => setShowFooter(!showFooter)}
      >
        <img
          src={DownIcon}
          alt="down-icon"
          style={{ transform: showFooter ? "rotate(0deg)" : "rotate(180deg)" }}
        />
      </button>

      <div
        className={`footer-main ${showFooter ? "" : "footer-hide-main"} ${
          isPlaybackMode ? "playback-mode" : ""
        }`}
      >
        <div className="container">
          <div className="footer-desc">
            <ul className="footer-list-align">
              <li className="d-flex">
                <div className="arrow-inline first-line-overall">
                  <h6>Overall</h6>
                  <h4>
                    {" "}
                    {isPlaybackMode && playbackDateForLabel
                      ? new Date(`${playbackDateForLabel}T00:00:00.000Z`).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            timeZone: "UTC",
                          }
                        )
                      : selectedDateRange.name === "All"
                      ? allRangeLabel(authReducer.authData?.createdAt)
                      : selectedDateRange.name === "Custom Date"
                      ? selectedEndDateRange
                        ? `${new Date(
                            selectedDateRange.value
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}  - 
                      ${new Date(selectedEndDateRange).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )}`
                        : `  
                      ${new Date(selectedDateRange.value).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )} - ${new Date().toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}`
                      : selectedDateRange.name}
                  </h4>
                </div>
              </li>
              <li className="metric-item metric-tip-1" data-tooltip-id="footer-metric-tooltip" data-tooltip-content={tip("pageViews", "Every page view in this range, including repeat views of the same page within a visit.")}>
                <div className="arrow-inline">
                  <h4>{historyDataInfo?.pageViews}</h4>
                  <div className="arrow-count">{trendArrows("pageViews")}</div>
                </div>
                {/* page_views_label (Title Case) for the label; page_views stays lowercase because it is also used inline after a number ("1,000 page views"). */}
                <p>{translation("page_views_label")}</p>
              </li>

              <li className="metric-item metric-tip-2" data-tooltip-id="footer-metric-tooltip" data-tooltip-content={tip("totalSession", `${SESSION_DEFINITION} Those visitors show under Total Visitors onsite.`)}>
                <div className="arrow-inline">
                  <h4>{historyDataInfo?.totalSession}</h4>
                  <div className="arrow-count">{trendArrows("totalSession")}</div>
                </div>
                <p>{translation("session")}</p>
              </li>

              <li className="metric-item metric-tip-3" data-tooltip-id="footer-metric-tooltip" data-tooltip-content={tip("percentNewSessions", "Share of ended sessions from visitors we had not seen before.")}>
                <div className="arrow-inline">
                  <h4>{historyDataInfo?.percentNewSessions}%</h4>
                  <div className="arrow-count">{trendArrows("percentNewSessions")}</div>
                </div>
                <p>% {translation("new_session")}</p>
              </li>

              <li className="metric-item metric-tip-4" data-tooltip-id="footer-metric-tooltip" data-tooltip-content={tip("pageviewsPerSession", "Average number of pages viewed per ended session.")}>
                <div className="arrow-inline">
                  <h4>{historyDataInfo?.pageviewsPerSession}</h4>
                  <div className="arrow-count">{trendArrows("pageviewsPerSession")}</div>
                </div>
                <p>{translation("pages")} / {translation("session")}</p>
              </li>

              <li className="metric-item metric-tip-5" data-tooltip-id="footer-metric-tooltip" data-tooltip-content={tip("bounceRate", "Ended sessions that saw one page, lasted under 20 seconds, and reached no goal.")}>
                <div className="arrow-inline">
                  <h4>{historyDataInfo?.bounceRate}</h4>
                  <div className="arrow-count">{trendArrows("bounceRate")}</div>
                </div>
                <p>{translation("bounce_rate")}</p>
              </li>

              <li className="metric-item metric-tip-6" data-tooltip-id="footer-metric-tooltip" data-tooltip-content={tip("avgSessionDuration", "Average length of an ended session, from first page to last.")}>
                <div className="arrow-inline">
                  <h4>
                    {averageSessionDurationConverter(
                      historyDataInfo?.avgSessionDuration
                    )}
                  </h4>
                  <div className="arrow-count">{trendArrows("avgSessionDuration")}</div>
                </div>
                <p>{translation("avg_session_duration")}</p>
              </li>

              <li className="metric-item metric-tip-7" data-tooltip-id="footer-metric-tooltip" data-tooltip-content={tip("conversions", "Ended sessions that reached one of your goal pages.")}>
                <div className="arrow-inline">
                  <h4>{historyDataInfo?.conversions}</h4>
                  <div className="arrow-count">{trendArrows("conversions")}</div>
                </div>
                <p>{translation("conversions")}</p>
              </li>

              <li className="metric-item metric-tip-8" data-tooltip-id="footer-metric-tooltip" data-tooltip-content={tip("conversionRate", "Share of ended sessions that reached a goal.")}>
                <div className="arrow-inline">
                  <h4>{historyDataInfo?.conversionRate}%</h4>
                  <div className="arrow-count">{trendArrows("conversionRate")}</div>
                </div>
                <p>{translation("conversion_rate")}</p>
              </li>
            </ul>
            {/* ONE tooltip answers every metric above through its data-tooltip-id / data-tooltip-content (a <Tooltip anchorSelect> each made eight MutationObservers on document.body that re-ran on every DOM change) */}
            <MetricTooltip id="footer-metric-tooltip" className="footer-metric-tooltip" />
          </div>
        </div>
      </div>
      { historicalLoader ? <Loader /> :""}
    </>
  );
};

export default Footer;
